import { ReactElement } from 'react';
import { Controller, FieldValues, UseFormRegister, UseFormReturn } from 'react-hook-form';
import { Autocomplete, RadioButton } from '@weareopenr/component-library';
import { IRange } from 'src/api/getRanges';
import { IMenuLabel } from 'src/api/getMenuLabels';
import { IFormData } from 'src/components/MenuModal/types';
import { EntitySource } from 'src/constants';
import styles from '../MenuModal/MenuModal.module.scss';

export interface IMenuSetSourceLabels {
  library: string;
  menuLabels: string;
  ranges: string;
}

interface IMenuSetSourceFieldProps {
  form: UseFormReturn<IFormData>;
  labels: IMenuSetSourceLabels;
  /** The brand's menu labels; only existing labels can be a source, as a new one has no menus yet. */
  menuLabels?: IMenuLabel[];
  /** Ranges only show where the brand has them switched on. */
  showRanges: boolean;
  ranges?: IRange[];
}

/** A menu set's Menu Source: All Menus, menus with any of the chosen labels, or menus in any of the chosen ranges. */
export const MenuSetSourceField = ({
  form,
  labels,
  menuLabels,
  showRanges,
  ranges,
}: IMenuSetSourceFieldProps): ReactElement => {
  const {
    watch,
    control,
    clearErrors,
    formState: { errors },
  } = form;
  const source = watch('source');
  // The library RadioButton takes an untyped form's register.
  const register = form.register as unknown as UseFormRegister<FieldValues>;

  return (
    <>
      <RadioButton name="source" value={EntitySource.LIBRARY} register={register}>
        {labels.library}
      </RadioButton>
      <RadioButton
        name="source"
        value={EntitySource.MENU_LABELS}
        register={register}
        disabled={!menuLabels || menuLabels.length === 0}
      >
        {labels.menuLabels}
      </RadioButton>
      {source === EntitySource.MENU_LABELS && (
        <div className={styles['autocomplete']}>
          <Controller
            control={control}
            name="sourceMenuLabels"
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                multiple
                name="sourceMenuLabels"
                label="Menu Labels"
                options={(menuLabels ?? []).map(({ menu_label_id, name }) => ({ value: menu_label_id, label: name }))}
                value={value ?? []}
                onChange={(menuLabelIds) => {
                  clearErrors('sourceMenuLabels');
                  onChange(menuLabelIds);
                }}
                limitChips={2}
                error={errors.sourceMenuLabels?.message?.toString()}
              />
            )}
          />
        </div>
      )}
      {showRanges && (
        <RadioButton
          name="source"
          value={EntitySource.RANGES}
          register={register}
          disabled={!ranges || ranges.length === 0}
        >
          {labels.ranges}
        </RadioButton>
      )}
      {showRanges && source === EntitySource.RANGES && (
        <div className={styles['autocomplete']}>
          <Controller
            control={control}
            name="ranges"
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                multiple
                name="ranges"
                label="Product Range"
                options={(ranges ?? []).map(({ range_id, name }) => ({ value: range_id, label: name }))}
                value={value ?? []}
                onChange={(rangeIds) => {
                  clearErrors('ranges');
                  onChange(rangeIds);
                }}
                limitChips={2}
                error={errors.ranges?.message?.toString()}
              />
            )}
          />
        </div>
      )}
    </>
  );
};

export default MenuSetSourceField;
